import { incidents } from '@cwos/api-client';
import type { Severity } from '@cwos/ui-shared';

/**
 * The Incidents screens' decisions, out of the components so they can be
 * asserted without mounting anything.
 *
 * THE ONE THING THIS FILE EXISTS FOR: `GET /api/incidents` HAS NO TOTAL AND NO
 * SCOPE FIELD. The rows are narrowed in SQL by reach — you read every incident,
 * or you filed it, or it names a client in your caseload — so an unreachable
 * incident is ABSENT, and the client is given nothing that says how far the
 * narrowing went. Every other list in this app can state its own frame from a
 * `scope` the server sends; this one cannot, and pretending otherwise would put
 * "3 incidents this month" under a heading that reads as organizational.
 *
 * So {@link incidentPopulation} answers from the CALLER'S OWN KEYS, and only
 * from the conjunction the server actually requires, and it has an UNKNOWN arm
 * for the case where those keys have not arrived. That is a mirror of a server
 * predicate — normally forbidden — and it is admissible here for exactly one
 * reason: the alternative is silence about a frame that is never organizational
 * for most readers. It is deliberately conservative in the direction that
 * matters: anything short of certainty renders the caveat, never the census.
 */

// ------------------------------------------------------------------ readings

export const CATEGORY_READING: Record<incidents.IncidentCategory, string> = {
  injury: 'Injury',
  medical_emergency: 'Medical emergency',
  medication: 'Medication',
  behavioral: 'Behavioural',
  elopement: 'Elopement',
  property_damage: 'Property damage',
  vehicle: 'Vehicle',
  allegation: 'Allegation',
  restraint: 'Restraint',
  death: 'Death',
  other: 'Other',
};

export const SEVERITY_READING: Record<
  incidents.IncidentSeverity,
  { variant: Severity; label: string }
> = {
  low: { variant: 'plain', label: 'Low' },
  moderate: { variant: 'sky', label: 'Moderate' },
  high: { variant: 'look', label: 'High' },
  critical: { variant: 'urgent', label: 'Critical' },
};

export const STATUS_READING: Record<
  incidents.IncidentStatus,
  { variant: Severity; label: string; meaning: string }
> = {
  reported: {
    variant: 'look',
    label: 'Reported',
    meaning: 'Filed. Nobody has picked it up yet.',
  },
  under_review: {
    variant: 'sky',
    label: 'Under review',
    meaning: 'Somebody is looking at it.',
  },
  action_taken: {
    variant: 'sky',
    label: 'Action taken',
    meaning: 'Something was done. It has not been written up as resolved.',
  },
  resolved: {
    variant: 'good',
    label: 'Resolved',
    meaning: 'Written up. Still open, so it can still be edited.',
  },
  closed: {
    variant: 'plain',
    label: 'Closed',
    meaning: 'Nothing further is owed. A closed incident can no longer be edited.',
  },
};

const EVENT_READING: Record<incidents.IncidentEventKind, string> = {
  reported: 'Reported',
  management_notified: 'Management notified',
  owner_assigned: 'Owner assigned',
  status_changed: 'Status changed',
  follow_up_raised: 'Follow-up raised',
  resolved: 'Resolved',
  closed: 'Closed',
  // In the database's CHECK list and emitted by NO code path. Given a reading
  // anyway: a row written by anything else must render as itself rather than
  // fall through to a wrong label.
  reopened: 'Reopened',
};

export function eventReading(kind: incidents.IncidentEventKind): string {
  return EVENT_READING[kind] ?? kind;
}

// ------------------------------------------------------------------- frame

/**
 * WHOSE incidents a list is over — answered from the caller's own keys, because
 * the server sends nothing.
 *
 *   'organization' — the reader holds `incidents:read_all` AND
 *                    `directory:read_all` at ORG scope. The server requires the
 *                    conjunction (`incidentReader`), so either key alone is a
 *                    narrowed reader and must not be read as org-wide.
 *   'narrowed'     — anything else. The rows are their own filings plus
 *                    incidents naming a client in their caseload.
 *   'unknown'      — the permission set has not arrived, or could not be read.
 *                    NOT 'narrowed' and NOT 'organization': both would be a
 *                    guess, and one of them is the guess that does damage.
 */
export type IncidentPopulation = 'organization' | 'narrowed' | 'unknown';

export interface IncidentScopeReader {
  /** The SCOPE-AWARE mirror of the server's `can()`, never the flattened `has`. */
  can: (key: string, scope?: { scopeType: 'org' | 'program' | 'location' }) => boolean;
  isLoading: boolean;
  isError: boolean;
}

export function incidentPopulation(reader: IncidentScopeReader): IncidentPopulation {
  if (reader.isLoading || reader.isError) return 'unknown';
  const readsEvery =
    reader.can(incidents.INCIDENTS_READ_ALL, { scopeType: 'org' }) &&
    reader.can('directory:read_all', { scopeType: 'org' });
  return readsEvery ? 'organization' : 'narrowed';
}

/** The caveat beside the list, or null when the reader really does see everything. */
export function incidentScopeNote(population: IncidentPopulation): string | null {
  switch (population) {
    case 'organization':
      return null;
    case 'narrowed':
      return 'incidents you filed, and incidents about a client on your caseload. An incident that names only a worker, a program or a location is not in this list at all';
    case 'unknown':
      return 'what your account reaches — we could not check how far that is';
  }
}

/**
 * The sentence for an EMPTY incident list, which is the dangerous one.
 *
 * "No incidents" on a compliance screen in a behavioural-health organization is
 * a safety claim, and for a narrowed reader it is false: it means none that THEY
 * can see. The three arms say three different things on purpose.
 */
export function emptyIncidentSentence(population: IncidentPopulation): string {
  switch (population) {
    case 'organization':
      return 'No incident has been reported in this organization. This is a complete answer — your account reaches every incident.';
    case 'narrowed':
      return 'You have not filed an incident, and none is recorded about a client on your caseload. This is NOT a statement about the organization: an incident naming only a worker, a program or a location does not appear here at all.';
    case 'unknown':
      return 'Nothing came back. We could not check how far your account reaches, so this is not evidence that there are no incidents.';
  }
}

// ------------------------------------------------------------------ pairings

/**
 * Has management been told?
 *
 * `incident_notified_pair_ck` pairs the time with the person, so there are two
 * states and not four — and the NEGATIVE one is a real fact worth showing on a
 * safety screen, not a blank cell.
 */
export type NotificationReading =
  { kind: 'notified'; who: string; when: string } | { kind: 'not-notified' };

export function notificationReading(incident: incidents.IncidentView): NotificationReading {
  if (incident.managementNotifiedAt === null || incident.managementNotified === null) {
    return { kind: 'not-notified' };
  }
  return {
    kind: 'notified',
    who: incident.managementNotified.displayName,
    when: incident.managementNotifiedAt,
  };
}

/**
 * Is there a follow-up, and can one be raised?
 *
 * `incident_follow_up_ck` makes `followUpRequired` exactly
 * `followUpTaskId IS NOT NULL`, so there is no "required but not raised" state:
 * raising it is what makes it required. One follow-up per incident, ever.
 */
export type FollowUpReading =
  { kind: 'raised'; taskId: string } | { kind: 'none' } | { kind: 'closed' };

export function followUpReading(incident: incidents.IncidentView): FollowUpReading {
  if (incident.followUpTaskId !== null) return { kind: 'raised', taskId: incident.followUpTaskId };
  return incident.status === 'closed' ? { kind: 'closed' } : { kind: 'none' };
}

/** A closed incident refuses every edit (409). Say so instead of offering one. */
export function isEditable(incident: incidents.IncidentView): boolean {
  return incident.status !== 'closed';
}

/**
 * Who this incident is about, in words.
 *
 * `incident_related_ck` guarantees at least one participant, so an empty result
 * is impossible from the server. Rendered as a sentence rather than a chip row
 * because "a client" and "a program" are different kinds of thing and the reach
 * rules turn on which — a client-less incident is invisible to most readers.
 */
export function participantSentence(incident: incidents.IncidentView): string {
  const parts: string[] = [];
  if (incident.clientId !== null) parts.push('a client');
  if (incident.worker !== null) parts.push(incident.worker.displayName);
  if (incident.programId !== null) parts.push('a program');
  if (incident.locationId !== null) parts.push('a location');
  return parts.length === 0 ? 'Nobody named' : parts.join(' · ');
}

// ------------------------------------------------------------------- format

export function formatWhen(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * The server's sentence, and never a status code.
 *
 * THE THREE COMMONEST 409s CARRY NO `reason` — "already closed", "already has a
 * follow-up", "a closed incident cannot be edited" are `IncidentStateError`,
 * which has no code — so branching is impossible and quoting is correct. The two
 * that DO carry one are the assignment refusals, and those get their own
 * sentence, because "the person you assigned this to cannot see what it is
 * about" is advice and the raw message is not.
 */
export function messageOf(err: unknown): string {
  const refusal = incidents.assignmentRefusal(err);
  if (refusal === 'assignee_lacks_participant_authority') {
    return 'That person cannot see what this incident is about, so the follow-up was not raised. Assign it to somebody on the client’s caseload, or take it yourself.';
  }
  if (refusal === 'incident_participant_absent') {
    return 'The follow-up was not raised: this incident no longer names anybody the assignment could be checked against.';
  }
  if (typeof err === 'object' && err !== null) {
    const message = (err as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim() !== '') return message.trim();
  }
  return 'That did not save. Try again.';
}
